import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import {useQuery, keepPreviousData} from "@tanstack/react-query";
import {TournamentsApi, TournamentDetailResponse as ApiTournamentDetailResponse, MatchResponse} from "@devour/client";
import getConfig from "@/utils/getConfig";
import {Tournament, TournamentTeam} from "@/competitions/tournaments/types";

export interface TournamentDetailResponse {
    tournament: Tournament;
    teams: TournamentTeam[];
    matches: MatchResponse[];
    /**
     * Server-computed id of the authenticated user's team, if any. The
     * embedded teams are summaries without rosters, so membership can't be
     * re-derived client-side — always prefer this field.
     */
    userTeamId?: string;
}

/**
 * Bridge the generated `TournamentDetailResponse` to the local view types in a
 * single place. `api.getTournamentsId` already returns a fully-typed response —
 * the only reason this isn't a direct assignment is that the tournaments module
 * still carries its own copy of the API types, whose enums are nominally
 * distinct from the generated ones. The shapes are otherwise identical, so this
 * is a structural re-view, not a data transform.
 *
 * `matches` is already the generated `MatchResponse[]` — no transform. The
 * tournament/teams casts remain until the module's broader type migration onto
 * @devour/client lands (see follow-up task).
 */
function toTournamentDetail(data: ApiTournamentDetailResponse): TournamentDetailResponse {
    return {
        tournament: data as unknown as Tournament,
        teams: (data.teams ?? []) as unknown as TournamentTeam[],
        matches: data.matches ?? [],
        userTeamId: data.userTeamId,
    };
}

/**
 * Hook to fetch a single tournament by ID with teams and matches
 *
 * @param tournamentId - The tournament ID to fetch
 * @returns React Query result with tournament detail data
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useGetTournament(tournamentId);
 * if (data) {
 *   console.log(data.tournament.name, data.teams.length);
 * }
 * ```
 */
export function useGetTournament(tournamentId?: string) {
    // useSelector must be called at the top level of the hook (Rules of Hooks)
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: [
            "tournament",
            tournamentId,
            fullToken?.id ?? "unauthenticated",
        ],
        queryFn: async (): Promise<TournamentDetailResponse> => {
            const api = new TournamentsApi(getConfig(fullToken));
            return toTournamentDetail(await api.getTournamentsId({id: tournamentId!}));
        },
        staleTime: 2 * 60000, // 2 minutes (more frequent updates for active tournaments)
        enabled: Boolean(tournamentId),
        placeholderData: keepPreviousData,
    });
}
